import React, { useState, useEffect } from 'react';
import ThemeSelector from '../SettingsPage/components/ThemeSelector/ThemeSelector';
import { useTheme } from '../../hooks/useTheme';
import './SettingsPage.css';

const SettingsPage = ({ showToast }) => {
  // We'll use theme to add conditional classes
  const { theme } = useTheme();
  const [isPowerSavingEnabled, setIsPowerSavingEnabled] = useState(
    localStorage.getItem('power-saving') === 'true'
  );
  const [autoSaveFrequency, setAutoSaveFrequency] = useState(
    localStorage.getItem('auto-save-frequency') || 'event'
  );
  const [exportFormat, setExportFormat] = useState(
    localStorage.getItem('export-format') || 'csv'
  );
  const [fontSize, setFontSize] = useState(
    localStorage.getItem('font-size') || 'normal'
  );

  // Handle power saving toggle
  const handlePowerSavingToggle = () => {
    const newValue = !isPowerSavingEnabled;
    setIsPowerSavingEnabled(newValue);
    localStorage.setItem('power-saving', newValue);
    
    // Check battery level immediately after enabling
    if (newValue && 'getBattery' in navigator) {
      navigator.getBattery().then(battery => {
        if (battery.level < 0.2) {
          // If battery is already low, apply power saving immediately
          document.documentElement.setAttribute('data-power-saving', 'true');
          if (showToast) {
            showToast('Power saving mode activated due to low battery', 'info');
          }
        }
      });
    } else {
      document.documentElement.removeAttribute('data-power-saving');
      if (showToast && !newValue) {
        showToast('Power saving mode disabled', 'info');
      }
    }
  };

  // Handle auto save frequency change
  const handleAutoSaveFrequencyChange = (e) => {
    const value = e.target.value;
    setAutoSaveFrequency(value);
    localStorage.setItem('auto-save-frequency', value);
    if (showToast) {
      showToast(`Auto-save frequency set to: ${value}`, 'success');
    }
  };
  
  // Handle export format change
  const handleExportFormatChange = (e) => {
    const value = e.target.value;
    setExportFormat(value);
    localStorage.setItem('export-format', value);
    if (showToast) {
      showToast(`Export format set to: ${value.toUpperCase()}`, 'success');
    }
  };
  
  // Handle font size changes
  const decreaseFontSize = () => {
    let newSize;
    switch (fontSize) {
      case 'normal': newSize = 'small'; break;
      case 'large': newSize = 'normal'; break;
      default: newSize = 'small';
    }
    setFontSize(newSize);
    localStorage.setItem('font-size', newSize);
    document.documentElement.setAttribute('data-font-size', newSize);
    if (showToast) {
      showToast(`Font size set to: ${newSize}`, 'info');
    }
  };
  
  const increaseFontSize = () => {
    let newSize;
    switch (fontSize) {
      case 'small': newSize = 'normal'; break;
      case 'normal': newSize = 'large'; break;
      default: newSize = 'large';
    }
    setFontSize(newSize);
    localStorage.setItem('font-size', newSize);
    document.documentElement.setAttribute('data-font-size', newSize);
    if (showToast) {
      showToast(`Font size set to: ${newSize}`, 'info');
    }
  };
  
  // Apply font size on component mount
  useEffect(() => {
    document.documentElement.setAttribute('data-font-size', fontSize);
    return () => {
      // Clean up
    };
  }, [fontSize]);

  // Add theme-specific class to the container
  const settingsPageClass = `settings-page ${theme === 'high-contrast' ? 'high-contrast' : ''}`;

  return (
    <div className={settingsPageClass}>
      <div className="settings-content">
        <section className="settings-section">
          <h2>Display</h2>
          
          <div className="setting-item">
            <div className="setting-label">
              <h3>Color Theme</h3>
              <p>Choose a color scheme that works best for your environment</p>
            </div>
            <ThemeSelector />
          </div>
          
          <div className="setting-item">
            <div className="setting-label">
              <h3>Font Size</h3>
              <p>Adjust text size for better readability</p>
            </div>
            <div className="font-size-controls">
              <button 
                className="btn-sm" 
                onClick={decreaseFontSize}
                disabled={fontSize === 'small'}
                aria-label="Decrease font size"
              >
                A-
              </button>
              <span className="current-size">
                {fontSize.charAt(0).toUpperCase() + fontSize.slice(1)}
              </span>
              <button 
                className="btn-sm" 
                onClick={increaseFontSize}
                disabled={fontSize === 'large'}
                aria-label="Increase font size"
              >
                A+
              </button>
            </div>
          </div>
        </section>
        
        <section className="settings-section">
          <h2>Power Saving</h2>
          
          <div className="setting-item">
            <div className="setting-label">
              <h3>Power Saving Mode</h3>
              <p>Automatically optimize display when battery is low</p>
            </div>
            <label className="toggle-switch">
              <input 
                type="checkbox" 
                checked={isPowerSavingEnabled}
                onChange={handlePowerSavingToggle}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
          
          <div className="battery-info setting-item">
            <div className="setting-label">
              <h3>Battery Information</h3>
            </div>
            <div className="battery-indicator">
              <BatteryIndicator />
            </div>
          </div>
        </section>
        
        <section className="settings-section">
          <h2>Data Management</h2>
          
          <div className="setting-item">
            <div className="setting-label">
              <h3>Auto-Save Frequency</h3>
              <p>How often to save match data automatically</p>
            </div>
            <select 
              value={autoSaveFrequency}
              onChange={handleAutoSaveFrequencyChange}
              className="settings-select"
            >
              <option value="event">After every event</option>
              <option value="1min">Every minute</option>
              <option value="5min">Every 5 minutes</option>
              <option value="manual">Manual save only</option>
            </select>
          </div>
          
          <div className="setting-item">
            <div className="setting-label">
              <h3>Export Format</h3>
              <p>Default file format for exporting match data</p>
            </div>
            <select 
              value={exportFormat}
              onChange={handleExportFormatChange}
              className="settings-select"
            >
              <option value="csv">CSV</option>
              <option value="json">JSON</option>
              <option value="xls">XLS</option>
            </select>
          </div>
        </section>
        
        <section className="settings-section">
          <h2>About</h2>
          <div className="about-content">
            <h3>Rugby Event Tracker</h3>
            <p>Version 1.0.0</p>
            <p>© 2025 Rugby Analytics Ltd.</p>
          </div>
        </section>
      </div>
    </div>
  );
};

// Battery Indicator Component
const BatteryIndicator = () => {
  const [batteryInfo, setBatteryInfo] = useState({
    level: null,
    charging: false,
    supported: 'getBattery' in navigator
  });

  useEffect(() => {
    if (!batteryInfo.supported) return;
    
    const updateBatteryInfo = (battery) => {
      setBatteryInfo({
        level: Math.floor(battery.level * 100),
        charging: battery.charging,
        supported: true
      });
    };

    const setupBatteryMonitoring = async () => {
      try {
        const battery = await navigator.getBattery();
        updateBatteryInfo(battery);
        
        // Set up event listeners
        battery.addEventListener('levelchange', () => updateBatteryInfo(battery));
        battery.addEventListener('chargingchange', () => updateBatteryInfo(battery));
        
        return () => {
          battery.removeEventListener('levelchange', () => updateBatteryInfo(battery));
          battery.removeEventListener('chargingchange', () => updateBatteryInfo(battery));
        };
      } catch (error) {
        console.error('Error accessing battery info:', error);
        setBatteryInfo(prev => ({ ...prev, supported: false }));
      }
    };

    setupBatteryMonitoring();
  }, [batteryInfo.supported]);

  if (!batteryInfo.supported) {
    return <div className="battery-unsupported">Battery information not available</div>;
  }

  if (batteryInfo.level === null) {
    return <div className="battery-loading">Loading battery information...</div>;
  }

  // Determine battery level class
  let levelClass = 'normal';
  if (batteryInfo.level <= 20) levelClass = 'low';
  else if (batteryInfo.level <= 50) levelClass = 'medium';

  return (
    <div className="battery-status">
      <div className={`battery-level ${levelClass} ${batteryInfo.charging ? 'charging' : ''}`}>
        <div 
          className="battery-fill" 
          style={{ width: `${batteryInfo.level}%` }}
        ></div>
        <div className="battery-percent">{batteryInfo.level}%</div>
      </div>
      <div className="battery-state">
        {batteryInfo.charging ? 'Charging' : 'Not charging'}
      </div>
    </div>
  );
};

export default SettingsPage;